import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import clsx from "clsx";
import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Button,
  MenuItem,
  IconButton,
  Menu,
  useTheme,
  useMediaQuery,
  Avatar,
  Badge,
  withStyles,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItem,
  ListItemAvatar,
  ListItemText,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import NotificationsIcon from "@material-ui/icons/Notifications";
import CachedIcon from "@material-ui/icons/Cached";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import api from "../services/api";
import MainListItems from "./MainListItems";
import SidebarWallet from "./SidebarWallet";
import NotificationsPopOver from "../components/NotificationsPopOver";
import ImportacaoNotificacao from "../components/ImportacaoNotificacao";
import NotificationsVolume from "../components/NotificationsVolume";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import toastError from "../errors/toastError";
import AnnouncementsPopover from "../components/AnnouncementsPopover";
import BirthdayModal, { isSnoozedToday } from "../components/BirthdayModal";
import logo from "../assets/logo.png";
import logoDark from "../assets/logo-black.png";
import ChatPopover from "../pages/Chat/ChatPopover";
import { useDate } from "../hooks/useDate";
import ColorModeContext from "../layout/themeContext";
import { getBackendUrl } from "../config";
import useSettings from "../hooks/useSettings";
import VersionControl from "../components/VersionControl";
import OnboardingAssistant from "../components/OnboardingAssistant";
import useSocketListener from "../hooks/useSocketListener";
import Tooltip from "@material-ui/core/Tooltip";
import HeaderSearch from "./header/HeaderSearch";
import LanguagePopover from "./header/LanguagePopover";
import AppearancePopover from "./header/AppearancePopover";
import UserMenu from "./header/UserMenu";
import PreferencesModal from "./header/PreferencesModal";
import useHeaderStyles from "./header/headerStyles";
import { greeting, planLabel, mostraPagarFatura, deveBuscarFaturaAberta } from "./header/headerRules";
import AvisoContaMeta from "./header/AvisoContaMeta";
import useAlturaObservada from "./header/useAlturaObservada";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import SubscriptionModal from "../components/SubscriptionModal";
import ReceiptOutlinedIcon from "@material-ui/icons/ReceiptOutlined";
import { logInfo, logError } from "../utils/logger";

const backendUrl = getBackendUrl();
const drawerWidth = 240;


const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    [theme.breakpoints.down("sm")]: {
      height: "calc(100vh - 56px)",
    },
    backgroundColor: theme.palette.fancyBackground,
    "& .MuiButton-outlinedPrimary": {
      color: theme.palette.primary.main, // Usa cor do tema
      border: `1px solid ${theme.palette.primary.main}40`,
      borderRadius: "8px",
      fontWeight: 600,
      textTransform: "none",
      transition: "all 0.3s ease",
      "&:hover": {
        backgroundColor: `${theme.palette.primary.main}10`,
        borderColor: theme.palette.primary.main,
        transform: "translateY(-1px)",
        boxShadow: `0 4px 12px ${theme.palette.primary.main}30`,
      },
    },
    "& .MuiTab-textColorPrimary.Mui-selected": {
      color: theme.palette.primary.main, // Usa cor do tema
      fontWeight: 700,
    },
  },

  chip: {
    background: "red",
    color: "white",
  },

  avatar: {
    width: "100%",
  },

  toolbar: {
    paddingLeft: 24,
    paddingRight: 20,
    gap: 8,
    // 375px: 44px de padding + ~13 itens de largura fixa estouravam a viewport
    // (pagina com 535px) e TODAS as telas ganhavam scroll horizontal.
    [theme.breakpoints.down("sm")]: { paddingLeft: 8, paddingRight: 8, gap: 4 },
    color: theme.palette.text.primary,
    background: theme.palette.aeros.surface,
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 60,
    transition: "all 0.3s ease",
  },
  greetWrap: { flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 10 },
  // Controles secundarios do cabecalho (idioma, aparencia, volume, atualizar e
  // separadores) somem no celular: continuam acessiveis em Preferencias (menu do
  // usuario) e o espaco fica para o que tem badge — notificacoes, avisos, chat.
  hideOnMobile: { display: "contents", [theme.breakpoints.down("sm")]: { display: "none" } },
  greet: { fontFamily: "'Space Grotesk', 'Inter', sans-serif", fontSize: 15, fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  plan: { fontSize: 11.5, color: theme.palette.text.secondary, lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  conn: { display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, color: theme.palette.type === "light" ? "#047857" : "#34D399", backgroundColor: "rgba(52,211,153,0.1)", borderRadius: 99, padding: "3px 10px", marginLeft: 8, whiteSpace: "nowrap", "& i": { width: 6, height: 6, borderRadius: "50%", background: "currentColor", animation: "$pulseDot 1.6s infinite" }, [theme.breakpoints.down("sm")]: { display: "none" } },
  connOff: { color: theme.palette.type === "light" ? "#92600A" : "#FBBF24", backgroundColor: "rgba(251,191,36,0.1)" },
  "@keyframes pulseDot": { "0%": { boxShadow: "0 0 0 0 rgba(52,211,153,0.6)" }, "100%": { boxShadow: "0 0 0 8px rgba(52,211,153,0)" } },
  vsep: { width: 1, height: 24, backgroundColor: theme.palette.type === "light" ? theme.palette.divider : "rgba(255,255,255,0.08)", margin: "0 4px", flex: "none" },

  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 8px",
    minHeight: "60px",
    backgroundColor: theme.palette.aeros.sidebar,
    [theme.breakpoints.down("sm")]: {
      height: "60px",
    },
    transition: "all 0.3s ease",
  },

  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  menuButtonHidden: {
    display: "none",
  },

  title: {
    flexGrow: 1,
    fontSize: 16,
    fontFamily: "'Space Grotesk', 'Inter', sans-serif",
    color: theme.palette.text.primary,
    fontWeight: 700,
    letterSpacing: "-0.02em",
  },

  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
    overflowY: "hidden",
    backgroundColor: theme.palette.aeros.sidebar,
    borderRight: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },

  drawerPaperClose: {
    overflowX: "hidden",
    overflowY: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
  },

  appBarSpacer: {
    minHeight: "60px",
  },

  content: {
    flex: 1,
    overflow: "auto",
    padding: 0,
    margin: 0,
  },

  container: {
    padding: 0,
    margin: 0,
    maxWidth: "none",
    width: "100%",
  },

  containerWithScroll: {
    flex: 1,
    overflowY: "scroll",
    overflowX: "hidden",
    ...theme.scrollbarStyles,
    borderRadius: "8px",
    border: "2px solid transparent",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    "-ms-overflow-style": "none",
    "scrollbar-width": "none",
  },

  NotificationsPopOver: {
    // Mantém original
  },

  logo: {
    width: "100%",
    //height: "45px",
    maxWidth: 180,
    [theme.breakpoints.down("sm")]: {
      width: "auto",
      height: "100%",
      maxWidth: 180,
    },
    logo: theme.logo,
    content:
      "url(" +
      (theme.mode === "light"
        ? theme.calculatedLogoLight()
        : theme.calculatedLogoDark()) +
      ")",
    transition: "all 0.3s ease", // Transição suave
    "&:hover": {
      transform: "scale(1.02)", // Pequeno zoom no hover
    },
  },

  hideLogo: {
    display: "none",
  },

  avatar2: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    cursor: "pointer",
    borderRadius: "50%",
    border: "2px solid transparent",
    background: `${theme.palette.aerosGradient} border-box`,
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "scale(1.05)",
    },
  },

  updateDiv: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },

  // Botões da toolbar melhorados
  toolbarButton: {
    color: theme.palette.text.secondary,
    borderRadius: "10px",
    border: `1px solid ${theme.palette.divider}`,
    padding: "6px",
    margin: "0 3px",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.aeros.surface2,
      color: theme.palette.text.primary,
    },
    "&:active": {
      transform: "translateY(0)",
    },
  },

  // Menu hambúrguer com animação sutil
  menuButton: {
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: theme.palette.aeros.surface2,
    },
    "& .MuiSvgIcon-root": {
      transition: "transform 0.3s ease",
    },
    "&:hover .MuiSvgIcon-root": {
      transform: "rotate(90deg)",
    },
  },

  // Seletor de idioma melhorado
  languageSelector: {
    position: "relative",
    display: "inline-block",
    "& > button": {
      background: theme.palette.aeros.surface2,
      border: "none",
      borderRadius: "8px",
      color: theme.palette.text.secondary,
      fontSize: "18px",
      padding: "8px 12px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      "&:hover": {
        background: theme.palette.aeros.border,
        color: theme.palette.text.primary,
        transform: "translateY(-1px)",
      },
    },
    "& > div": {
      position: "absolute",
      top: "45px",
      left: "0",
      background: theme.palette.background.paper,
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      borderRadius: "8px",
      padding: "8px",
      zIndex: 1000,
      minWidth: "120px",
      "& button": {
        background: "none",
        border: "none",
        color: theme.palette.text.primary,
        display: "block",
        width: "100%",
        padding: "8px 12px",
        textAlign: "left",
        borderRadius: "6px",
        fontSize: "14px",
        fontWeight: 500,
        transition: "all 0.2s ease",
        "&:hover": {
          background: `${theme.palette.primary.main}10`, // Usa cor do tema
          color: theme.palette.primary.main, // Usa cor do tema
          transform: "none",
        },
      },
    },
  },

  // Badge animado
  animatedBadge: {
    "& .MuiBadge-badge": {
      animation: "$heartbeat 2s infinite",
    },
  },

  "@keyframes heartbeat": {
    "0%": { transform: "scale(1)" },
    "14%": { transform: "scale(1.1)" },
    "28%": { transform: "scale(1)" },
    "42%": { transform: "scale(1.1)" },
    "70%": { transform: "scale(1)" },
  },
}));

const StyledBadge = withStyles((theme) => ({
  badge: {
    backgroundColor: "#44b700",
    color: "#44b700",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "$ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  "@keyframes ripple": {
    "0%": {
      transform: "scale(.8)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(2.4)",
      opacity: 0,
    },
  },
}))(Badge);

const SmallAvatar = withStyles((theme) => ({
  root: {
    width: 22,
    height: 22,
    border: `2px solid ${theme.palette.background.paper}`,
  },
}))(Avatar);

const LoggedInLayout = ({ children, themeToggle, hideMenu = false }) => {
  const classes = useStyles();
  const [refFaixaConta, alturaFaixaConta] = useAlturaObservada();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading, user, socket } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerVariant, setDrawerVariant] = useState("permanent");

  const [showAnnouncementsModal, setShowAnnouncementsModal] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [ackChecked, setAckChecked] = useState(false);
  const [showBirthdayModal, setShowBirthdayModal] = useState(false);

  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const { whatsApps: conexoesWhats } = useContext(WhatsAppsContext);
  const greaterThenSm = useMediaQuery(theme.breakpoints.up("sm"));

  const [volume, setVolume] = useState(localStorage.getItem("volume") || 1);
  const headerClasses = useHeaderStyles();
  const [socketConnected, setSocketConnected] = useState(true);
  const [presence, setPresence] = useState(localStorage.getItem("presence") || "online");
  const [prefsOpen, setPrefsOpen] = useState(false);
  useEffect(() => {
    const raw = socket?.socket || socket;
    if (!raw || typeof raw.on !== "function") return undefined;
    const on = () => setSocketConnected(true);
    const off = () => setSocketConnected(false);
    if (typeof raw.connected === "boolean") setSocketConnected(raw.connected);
    raw.on("connect", on); raw.on("disconnect", off);
    return () => { raw.off && raw.off("connect", on); raw.off && raw.off("disconnect", off); };
  }, [socket]);
  // Botão "Pagar fatura": só o admin busca a fatura aberta. Falha aqui é
  // silenciosa — o cabeçalho nunca deve incomodar com toast.
  const buscaFatura = deveBuscarFaturaAberta(user);
  const [faturaAberta, setFaturaAberta] = useState(null);
  const [pagarOpen, setPagarOpen] = useState(false);
  useEffect(() => {
    if (!buscaFatura) { setFaturaAberta(null); return undefined; }
    let ativo = true;
    api.get("/invoices/open")
      .then(({ data }) => { if (ativo) setFaturaAberta(data || null); })
      .catch(() => {});
    return () => { ativo = false; };
  }, [buscaFatura, user?.companyId]);
  useEffect(() => {
    const raw = socket?.socket || socket;
    if (!buscaFatura || !user?.companyId || !raw || typeof raw.on !== "function") return undefined;
    const evento = `company-${user.companyId}-payment`;
    const onPayment = (data) => {
      if (data?.action !== "CONCLUIDA") return;
      setFaturaAberta(null);
      setPagarOpen(false);
    };
    raw.on(evento, onPayment);
    return () => { raw.off && raw.off(evento, onPayment); };
  }, [socket, buscaFatura, user?.companyId]);
  const mostraPagar = mostraPagarFatura({
    company: { ...(user?.company || {}), id: user?.company?.id ?? user?.companyId },
    ...(faturaAberta || {}),
  });

  const handlePresence = async (p) => {
    setPresence(p);
    localStorage.setItem("presence", p);
    try { await api.put(`/users/${user.id}/presence`, { presence: p }); } catch (e) { /* backend opcional */ }
  };

  const { dateToClient } = useDate();
  const [profileUrl, setProfileUrl] = useState(null);
  const [updateInProgress, setUpdateInProgress] = useState(false);


  // eslint-disable-next-line react-hooks/exhaustive-deps
  const mainListItems = useMemo(
    () => <MainListItems drawerOpen={drawerOpen} collapsed={!drawerOpen} />,
    [user, drawerOpen]
  );

  const settings = useSettings();

  const fetchAnnouncements = useCallback(async () => {
      try {
        const { data } = await api.get("/announcements/for-company", {
          params: {
            status: true,
            pageNumber: "1"
          }
        });

        // Filtra apenas os informativos ativos e não expirados
        const activeAnnouncementsRaw = data.records.filter(announcement => {
          const isActive = announcement.status === true || announcement.status === "true";
          const isNotExpired = !announcement.expiresAt || new Date(announcement.expiresAt) > new Date();
          return isActive && isNotExpired;
        });

        // Backend já filtra por empresa excluindo os reconhecidos
        const activeAnnouncements = activeAnnouncementsRaw;
        setAnnouncements(activeAnnouncements);
        setShowAnnouncementsModal(activeAnnouncements.length > 0);
      } catch (err) {
        toastError(err);
      }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      fetchAnnouncements();
    }
  }, [user?.id, fetchAnnouncements]);

  // Atualiza checkbox ao trocar de aviso
  useEffect(() => {
    if (!selectedAnnouncement) {
      setAckChecked(false);
      return;
    }
    // Não precisamos mais ler localStorage; tratamos via backend
    setAckChecked(false);
  }, [selectedAnnouncement, user?.companyId]);

  const handleToggleAcknowledge = async (announcementId, checked) => {
    try {
      if (checked) {
        await api.post(`/announcements/${announcementId}/ack`);
        // Remove este aviso da lista
        setAnnouncements(prev => prev.filter(a => a.id !== announcementId));
        setSelectedAnnouncement(null);
      } else {
        await api.delete(`/announcements/${announcementId}/ack`);
        // Opcional: recarregar lista para reexibir (se desejar permitir desfazer)
        await fetchAnnouncements();
      }
      // Fecha modal se não restarem avisos
      setShowAnnouncementsModal((prev) => {
        return announcements.length - 1 > 0;
      });
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    

    if (document.body.offsetWidth > 600) {
      if (user.defaultMenu === "closed") {
        setDrawerOpen(false);
      } else {
        setDrawerOpen(true);
      }
    }
    if (user.defaultTheme === "dark" && theme.mode === "light") {
      colorMode.toggleColorMode();
    }
  }, [user.defaultMenu, document.body.offsetWidth]);

  useEffect(() => {
    if (document.body.offsetWidth < 600) {
      setDrawerVariant("temporary");
    } else {
      setDrawerVariant("permanent");
    }
  }, [drawerOpen]);

  useEffect(() => {
    const companyId = user?.companyId;

    if (companyId) {
      const buildProfileUrl = () => {
        const savedProfileImage = localStorage.getItem("profileImage");
        const currentProfileImage = savedProfileImage || user.profileImage;

        if (currentProfileImage) {
          return `${backendUrl}/public/company${companyId}/user/${currentProfileImage}`;
        }
        return `${backendUrl}/public/app/noimage.png`;
      };

      setProfileUrl(buildProfileUrl());
    }
  }, [user?.companyId, user?.profileImage, backendUrl]);

  // Callbacks dos eventos
  const handleAuthEvent = useCallback((data) => {
    if (data.user.id === +user?.id) {
      toastError("Sua conta foi acessada em outro computador.");
      setTimeout(() => {
        localStorage.clear();
        window.location.reload();
      }, 1000);
    }
  }, [user?.id]);

  const handleUserUpdate = useCallback((data) => {
    if (data.action === "update" && data.user.id === +user?.id) {
      if (data.user.profileImage) {
        const newProfileUrl = `${backendUrl}/public/company${user?.companyId}/user/${data.user.profileImage}`;
        setProfileUrl(newProfileUrl);
        localStorage.setItem("profileImage", data.user.profileImage);
      }
    }
  }, [user?.companyId, user?.id, backendUrl]);

  // Callbacks para eventos de aniversário
  const handleUserBirthday = useCallback((data) => {
    logInfo("Evento de aniversário de usuário recebido", { data });
    // "Lembrar mais tarde" vale para o dia todo neste navegador.
    if (data.userId === +user?.id && !isSnoozedToday()) {
      setShowBirthdayModal(true);
    }
  }, [user?.id]);

  const handleContactBirthday = useCallback((data) => {
    logInfo("Evento de aniversário de contato recebido", { data });
  }, []);

  // Verificar aniversários no login
  const checkBirthdaysOnLogin = useCallback(async () => {
    if (user?.id && user?.companyId) {
      try {
        const { data } = await api.get("/birthdays/today");
        const birthdayData = data.data;

        // Verificar se o usuário atual faz aniversário hoje
        const userBirthday = birthdayData.users.find(u => u.id === +user.id);
        if (userBirthday && !isSnoozedToday()) {
          logInfo("Usuário faz aniversário hoje; exibindo modal");
          setShowBirthdayModal(true);
        }

        // Se há aniversariantes, mostrar notificação
        if (birthdayData.users.length > 0 || birthdayData.contacts.length > 0) {
          logInfo("Há aniversariantes hoje", { birthdayData });
        }
      } catch (error) {
        logError("Erro ao verificar aniversários", error);
      }
    }
  }, [user?.id, user?.companyId]);

  // Registrar listeners
  useSocketListener(socket, user, 'auth', handleAuthEvent);
  useSocketListener(socket, user, 'user', handleUserUpdate);
  useSocketListener(socket, user, 'user-birthday', handleUserBirthday);
  useSocketListener(socket, user, 'contact-birthday', handleContactBirthday);

  // Verificar aniversários quando o usuário faz login
  useEffect(() => {
    if (user?.id && user?.companyId) {
      // Pequeno delay para garantir que o socket esteja conectado
      const timer = setTimeout(() => {
        checkBirthdaysOnLogin();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [user?.id, user?.companyId, checkBirthdaysOnLogin]);

  // Status do usuário
  useEffect(() => {
    if (socket?.emit && user?.companyId) {
      socket.emit("userStatus");

      const interval = setInterval(() => {
        socket?.emit && socket.emit("userStatus");
      }, 1000 * 60 * 5);

      return () => clearInterval(interval);
    }
  }, [socket, user?.companyId]);

  const handleUpdateStart = () => {
    setUpdateInProgress(true);
  };

  const handleUpdateComplete = () => {
    setUpdateInProgress(false);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const drawerClose = () => {
    if (document.body.offsetWidth < 600 || user.defaultMenu === "closed") {
      setDrawerOpen(false);
    }
  };

  const handleRefreshPage = () => {
    window.location.reload(false);
  };

  const handleMenuItemClick = () => {
    const { innerWidth: width } = window;
    if (width <= 600) {
      setDrawerOpen(false);
    }
  };

  const handleLanguageChange = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("language", lng);
  };

  const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en"]);
  const { getAll } = useSettings();
  useEffect(() => {
    async function fetchSettings() {
      try {
        const settings = await getAll();
        const enabledLanguagesSetting = settings.find(
          (s) => s.key === "enabledLanguages"
        )?.value;
        let langs = ["pt-BR", "en"];
        try {
          if (enabledLanguagesSetting) {
            langs = JSON.parse(enabledLanguagesSetting);
          }
        } catch { }
        console.log(
          "Layout - enabledLanguages carregadas:",
          langs,
          "para companyId:",
          user?.companyId
        );
        setEnabledLanguages(langs);
      } catch (error) {
        console.log("Layout - erro ao carregar enabledLanguages:", error);
      }
    }
    fetchSettings();
  }, [user?.companyId]);


  if (loading || updateInProgress) {
    return <BackdropLoading />;
  }

  return (
    <div className={clsx(classes.root, "logged-in-layout")}>
      {!hideMenu && (
        <Drawer
          variant={drawerVariant}
          className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
          classes={{
            paper: clsx(
              classes.drawerPaper,
              !drawerOpen && classes.drawerPaperClose
            ),
          }}
          open={drawerOpen}
        >
          <div className={classes.toolbarIcon}>
            <img
              className={drawerOpen ? classes.logo : classes.hideLogo}
              style={{
                display: "block",
                margin: "0 auto",
                //height: "50px",
                width: "100%",
              }}
              alt="logo"
            />
            <IconButton onClick={() => setDrawerOpen(!drawerOpen)}>
              <ChevronLeftIcon />
            </IconButton>
          </div>
          <List className={classes.containerWithScroll}>
            {/* {mainListItems} */}
            <MainListItems collapsed={!drawerOpen} />
          </List>
          <SidebarWallet collapsed={!drawerOpen} />
        </Drawer>
      )}

      <AppBar
        position="absolute"
        className={clsx(classes.appBar, !hideMenu && drawerOpen && classes.appBarShift)}
        color="inherit"
        elevation={0}
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          {!hideMenu && (
            <IconButton
              edge="start"
              aria-label="open drawer"
              style={{ color: theme.palette.text.primary }}
              onClick={() => setDrawerOpen(!drawerOpen)}
              className={clsx(drawerOpen && classes.menuButtonHidden)}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Saudação curta + plano + status da conexão (mockup Aeros) */}
          <div className={classes.greetWrap}>
            <div style={{ minWidth: 0 }}>
              <div className={classes.greet}>{greeting(user?.name)}</div>
              <div className={classes.plan}>{planLabel(user?.company, dateToClient)}</div>
            </div>
            {mostraPagar && (
              <Tooltip title={i18n.t("aeros.header.payInvoice")}>
                <button
                  type="button"
                  className={headerClasses.payBtn}
                  aria-label={i18n.t("aeros.header.payInvoice")}
                  onClick={() => setPagarOpen(true)}
                >
                  <ReceiptOutlinedIcon aria-hidden="true" />
                  <span className={headerClasses.payLabel}>{i18n.t("aeros.header.payInvoice")}</span>
                </button>
              </Tooltip>
            )}
            <span className={`${classes.conn} ${socketConnected ? "" : classes.connOff}`}>
              <i />{socketConnected ? i18n.t("aeros.header.connected") : i18n.t("aeros.header.reconnecting")}
            </span>
          </div>

          {hideMenu && (
            // Licença vencida: sem menu/atalhos, mas o usuário precisa poder sair.
            <button
              type="button"
              className={headerClasses.logoutBtn}
              aria-label={i18n.t("mainDrawer.appBar.user.logout")}
              onClick={handleClickLogout}
            >
              <ExitToAppIcon aria-hidden="true" />
              <span className={headerClasses.logoutLabel}>{i18n.t("mainDrawer.appBar.user.logout")}</span>
            </button>
          )}

          {!hideMenu && (
            <>
              <VersionControl
                onUpdateStart={handleUpdateStart}
                onUpdateComplete={handleUpdateComplete}
              />
              <HeaderSearch />
              <span className={classes.hideOnMobile}>
                <div className={classes.vsep} />
                <LanguagePopover enabled={enabledLanguages} onChange={handleLanguageChange} />
                <AppearancePopover />
                <NotificationsVolume setVolume={setVolume} volume={volume} />
              </span>
              {user.id && <NotificationsPopOver volume={volume} />}
              <AnnouncementsPopover />
              <ChatPopover />
              <span className={classes.hideOnMobile}>
                <Tooltip title={i18n.t("mainDrawer.appBar.refresh")}>
                  <IconButton onClick={handleRefreshPage} className={headerClasses.iconBtn} aria-label={i18n.t("mainDrawer.appBar.refresh")}>
                    <CachedIcon />
                  </IconButton>
                </Tooltip>
              </span>
              {user.id && <ImportacaoNotificacao />}
              <span className={classes.hideOnMobile}>
                <div className={classes.vsep} />
              </span>
              <UserMenu
                user={user}
                profileUrl={profileUrl}
                presence={presence}
                onPresence={handlePresence}
                onProfile={handleOpenUserModal}
                onPreferences={() => setPrefsOpen(true)}
                onLogout={handleClickLogout}
              />
              {prefsOpen && (
                <PreferencesModal
                  open
                  onClose={() => setPrefsOpen(false)}
                  user={user}
                  volume={volume}
                  setVolume={setVolume}
                  enabledLanguages={enabledLanguages}
                  onLanguage={handleLanguageChange}
                  onMenuChange={(v) => setDrawerOpen(v === "open")}
                />
              )}
              <UserModal
                open={userModalOpen}
                onClose={() => setUserModalOpen(false)}
                onImageUpdate={(newProfileUrl) => setProfileUrl(newProfileUrl)}
                userId={user?.id}
              />
            </>
          )}
        </Toolbar>
      </AppBar>
      {faturaAberta?.invoice && (
        <SubscriptionModal
          open={pagarOpen}
          onClose={() => setPagarOpen(false)}
          Invoice={faturaAberta.invoice}
        />
      )}
      <main
        className={classes.content}
        style={{ "--faixa-conta-h": `${alturaFaixaConta}px` }}
      >
        <div className={classes.appBarSpacer} />
        {/* A faixa entra no fluxo do <main>. As telas de altura cheia
            (Conversas, Chat interno) calculam a altura em % do <main> e
            descontam --faixa-conta-h; sem faixa a variavel vale 0px. */}
        <div ref={refFaixaConta}>
          <AvisoContaMeta user={user} whatsApps={conexoesWhats} />
        </div>
        {children ? children : null}
        <OnboardingAssistant />
      </main>

      {/* Modal de Informativos */}
      <Dialog
        open={showAnnouncementsModal}
        onClose={() => setShowAnnouncementsModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Informativos</DialogTitle>
        <DialogContent dividers>
          {selectedAnnouncement ? (
            <div>
              <Typography variant="h6" gutterBottom>
                {selectedAnnouncement.title}
              </Typography>
              <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                {selectedAnnouncement.text}
              </Typography>
              <FormControlLabel
                style={{ marginTop: 12 }}
                control={
                  <Checkbox
                    color="primary"
                    checked={ackChecked}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setAckChecked(checked);
                      handleToggleAcknowledge(selectedAnnouncement.id, checked);
                    }}
                  />
                }
                label="Estou ciente e não mostrar novamente"
              />
              {selectedAnnouncement.mediaPath && (
                <div style={{ marginTop: 16 }}>
                  <img
                    src={`${backendUrl}/public/company${user.companyId}${selectedAnnouncement.mediaPath}`}
                    alt="Anexo"
                    style={{ maxWidth: '100%' }}
                  />
                </div>
              )}
              <Button
                onClick={() => setSelectedAnnouncement(null)}
                style={{ marginTop: 16 }}
                variant="outlined"
              >
                Voltar para lista
              </Button>
            </div>
          ) : (
            <List>
              {announcements.map((announcement) => (
                <ListItem
                  button
                  key={announcement.id}
                  onClick={() => setSelectedAnnouncement(announcement)}
                >
                  <ListItemAvatar>
                    <Avatar>
                      <NotificationsIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={announcement.title}
                    secondary={
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          color="textPrimary"
                        >
                          Prioridade: {announcement.priority === 1 ? 'Alta' : announcement.priority === 2 ? 'Média' : 'Baixa'}
                        </Typography>
                        {` — ${new Date(announcement.createdAt).toLocaleDateString()}`}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowAnnouncementsModal(false)}
            color="primary"
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Aniversário */}
      <BirthdayModal
        open={showBirthdayModal}
        onClose={() => setShowBirthdayModal(false)}
        user={user}
      />

    </div>
  );
};

export default LoggedInLayout;
